/**
 * Agent Spend Cap Service (BL-37669b48)
 * AIDLC Administrative Backend
 *
 * One cap for every agent run: today's and this month's spend (America/Detroit) against it,
 * the CEO's raise for the current day or month, and whether the cap is enforced at all
 * (CAP-TOGGLE-1, BL-b544aa26).
 */

import apiClient from './api';

export type CapPeriod = 'day' | 'month';
export type CapLevel = 'ok' | 'warning' | 'reached';

export interface PeriodCapState {
  period: CapPeriod;
  key: string;
  startsAt: string;
  spentUsd: number;
  capUsd: number;
  defaultCapUsd: number;
  pct: number;
  level: CapLevel;
  raise: { capUsd: number; raisedBy: string; raisedAt: string; reason: string } | null;
}

/** Who last turned enforcement on or off, when and why. */
export interface EnforcementChange {
  enforce: boolean;
  changedBy: string;
  changedByEmail: string;
  changedAt: string;
  reason: string;
}

export interface SpendCapState {
  day: PeriodCapState;
  month: PeriodCapState;
  level: CapLevel;
  warningPct: number;
  sanityMaxUsd: Record<CapPeriod, number>;
  checkedAt: string;
  /** False: caps warn but never refuse a session. */
  enforce: boolean;
  /** null while enforcement has never been changed (on by default). */
  enforcement: EnforcementChange | null;
  refusesNewSessions: boolean;
}

/** The reason the server accepts for turning enforcement on or off (spend-cap-enforcement.ts). */
export const MIN_ENFORCEMENT_REASON_CHARS = 8;
export const MAX_ENFORCEMENT_REASON_CHARS = 500;

export async function fetchSpendCap(): Promise<SpendCapState> {
  const response = await apiClient.get<SpendCapState>('/spend-cap');
  return response.data;
}

export async function raiseSpendCap(period: CapPeriod, capUsd: number, reason: string): Promise<SpendCapState> {
  const response = await apiClient.post<SpendCapState>('/spend-cap/raise', { period, capUsd, reason });
  return response.data;
}

export async function setSpendCapEnforcement(enforce: boolean, reason: string): Promise<SpendCapState> {
  const response = await apiClient.put<SpendCapState>('/spend-cap/enforcement', { enforce, reason });
  return response.data;
}

/** $12.50: spend, always to the cent. */
export const dollars = (usd: number): string => `$${usd.toFixed(2)}`;
/** $25, or $25.50: a cap, without cents when it has none. */
export const capDollars = (usd: number): string => (Number.isInteger(usd) ? `$${usd}` : dollars(usd));

/** The server's refusal, in words fit to show. */
export function spendCapErrorText(err: unknown, fallback: string): string {
  const fromServer = (err as { response?: { data?: { error?: string; details?: string[] } } })?.response?.data;
  return fromServer?.details?.join('; ') || fromServer?.error || (err as Error)?.message || fallback;
}
